import { type FC, useCallback, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  useBuildFactoryMutation,
  useGetFactoriesQuery,
} from '@api/queries/factory';
import FirstBuild from '@common/components/FirstBuild/FirstBuild';
import { useCheckRequirements } from '@common/hooks/check-requirements';
import { updateStorageAndBalance } from '@redux/reducers/userReducer';
import { selectFactoryNextToBuildRequirements } from '@redux/selectors/factoryReducer';
import { useAppDispatch, useAppSelector } from '@redux/store';

import { completeNewLevelStep } from '@/common/lib/completeNewLevelStep';
import { firebaseLogUpgradeBuilding } from '@/common/lib/firebaseLog';
import { SendAnalitics } from '@/common/lib/playdeck';
import { type PlantTypeText } from '@/common/types/plant';
import { selectNextStepPlant } from '@/redux/selectors/userSelectors';

import { FactoryBuildComplete } from '../FactoryBuildComplete/FactoryBuildComplete';

const FactoryBuild: FC = () => {
  const [skip, setSkip] = useState(false);
  useGetFactoriesQuery(undefined, { skip, refetchOnMountOrArgChange: true });
  const isFirstBuild = useAppSelector(
    (state) => state.factory.list?.length === 0,
  );
  const [isConstructComplete, setIsConstructComplete] = useState(false);
  const [buildFactory, { isLoading }] = useBuildFactoryMutation();
  const nextToBuild = useAppSelector((state) => state.factory.nextToBuild);
  const requirementsList = useAppSelector(selectFactoryNextToBuildRequirements);
  const { isComplete } = useCheckRequirements(
    nextToBuild?.creationRequirements,
  );
  const currentStep = useAppSelector(selectNextStepPlant);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const onBuild = useCallback((): void => {
    if (!nextToBuild) return;
    const { id: item_id, name: item, level: item_level } = nextToBuild;

    setSkip(true);
    buildFactory({ id: item_id }).then(() => {
      SendAnalitics.buildDone({
        type: 'materials',
        item_id,
        item: item as PlantTypeText,
        item_level,
      });
      dispatch(updateStorageAndBalance(requirementsList));
      setIsConstructComplete(true);
      completeNewLevelStep({
        data: { name: nextToBuild?.name, level: 1 },
        dispatch,
        currentStep: {
          id: currentStep?.id,
          name: currentStep?.name,
          level: currentStep?.level,
        },
      });
    });

    if (nextToBuild && nextToBuild.creationRequirements)
      firebaseLogUpgradeBuilding(
        {
          name: nextToBuild.name,
          id: nextToBuild.id,
          level: nextToBuild.level,
        },
        nextToBuild.creationRequirements,
      );
  }, [
    buildFactory,
    dispatch,
    setIsConstructComplete,
    requirementsList,
    nextToBuild,
    navigate,
  ]);

  if (!nextToBuild || !requirementsList) {
    return null;
  }

  return isConstructComplete ? (
    <FactoryBuildComplete name={nextToBuild.name} />
  ) : (
    <>
      <Helmet>
        <title>Factory_Build</title>
      </Helmet>
      <FirstBuild
        isComplete={isComplete}
        requirementsList={requirementsList}
        kind="factory"
        isFirstBuild={isFirstBuild}
        name={nextToBuild.name}
        onBuild={onBuild}
        isLoading={isLoading}
      />
    </>
  );
};

export { FactoryBuild };
